import type {
  PortalComparisonMode,
  PortalCompetitivePosition,
  PortalProvenanceLine,
  PortalReportLabels,
} from '../../types/portalReport.js';
import type { ChartGrain } from './chartPlan.js';
import { PORTAL_COMPARISON_PHRASE } from './comparison.js';

// CLIENT PORTAL RESTYLE — every sentence the page states, as pure functions.
//
// ⚠️ PURE ON PURPOSE, so each one is asserted WITHOUT jsdom. The suite already
// sits one jsdom file from red (a 651st render file reproducibly tips several
// unrelated heavy tests over the 5s timeout), and the wording is the part worth
// pinning anyway — a chart that draws correctly under a footnote claiming the
// opposite is still a report that misleads.

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE CHART FOOTNOTE FAMILY WAS REMOVED HERE (2026-09-01), AND SHOULD NOT
// COME BACK.
//
// `chartFootnote`, `costPerLeadBarCaveat` and the delivery ratios' gap sentence
// explained how a chart worked, under every chart. Five delivery cards each
// carrying a paragraph about bars and gaps is the repetition the copy pass two
// rounds ago existed to end. Scott: *"just remove them."*
//
// ⚠️ WHAT CARRIES THE MEANING NOW is the chart itself: the tooltip says "not
// measured" on a null bucket and "· partial" on an unfinished one, and an
// unmeasured span is shaded and captioned on the canvas. That was CONFIRMED
// before the sentences were cut, not assumed — if it had not been, the
// information would have moved into the tooltip rather than back into a
// footnote.
//
// ⚠️⚠️ REMOVING THE "stacked, so the column height is the month's total"
// sentence does NOT license drawing a partial stacked column as if it were
// complete. That intuition comes from the FORM, not from the caption. See
// `StackedBars` in ReportTrendChart.tsx.
//
// ⚠️ `deliveryHistoryWithheld` below is NOT one of these: it is a fact about the
// DATA (there is not enough of it to chart), in the place the chart would be.
// So is `built.withheldReason` in ReportChartCard.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The period line
// ---------------------------------------------------------------------------

/**
 * The header's period sentence.
 *
 * ⚠️ IT SAYS THE PERIOD EXCLUDES TODAY, because the default does and a reader
 * comparing the page against their ad account at 4pm would otherwise think we
 * were under-reporting. `asOf` is stated unconditionally — silence about
 * freshness implies live, and Google's transfer lands a day behind Meta's.
 */
export function periodNote(
  range: { from: string; to: string },
  today: string,
  formatDate: (iso: string) => string,
  comparison?: { range: { from: string; to: string }; unavailableReason: string | null } | null,
): string {
  const span = `${formatDate(range.from)} – ${formatDate(range.to)}`;
  // ⚠️ THE FACT, NOT THE REASONING. Why a running day drags an average down is
  // our arithmetic, not the client's business; THAT it is excluded is the part
  // that changes how they read the figures. The included-today branch keeps its
  // second clause because there the consequence IS the fact — the figures are
  // incomplete, which is not derivable from "today is included".
  const period =
    range.to < today
      ? `${span}. Today is excluded.`
      : `${span}. Today is included and is still running, so its figures are incomplete.`;
  return `${period}${comparisonClause(comparison, formatDate)}`;
}

/**
 * The comparison window, stated next to the period it is compared against.
 *
 * ⚠️⚠️ EVERY DELTA ON THE PAGE IS AGAINST THIS WINDOW AND NOTHING NAMED IT.
 * The header said "1–31 Aug 2026" and the figures carried percentages against
 * a window the reader could only infer from a dropdown they may not have
 * touched — and the default is year-on-year, which is not what most readers
 * assume a percentage means.
 *
 * ⚠️⚠️ THE RANGE IS READ FROM THE PAYLOAD, never recomputed here. The server
 * resolves it (it decides what to fetch) and a browser-side re-derivation would
 * be a second definition of every comparison mode, which is the drift
 * `portalComparisonRange` exists to prevent arriving one layer later.
 *
 * ⚠️ AN UNAVAILABLE COMPARISON PRINTS ITS REASON, NEVER ITS DATES. Naming a
 * window nothing was compared against is worse than the silence it replaced:
 * the reader would take the absent percentages for "no change". The reasons are
 * already whole sentences naming the comparison period, so they need no prefix.
 */
function comparisonClause(
  comparison: { range: { from: string; to: string }; unavailableReason: string | null } | null | undefined,
  formatDate: (iso: string) => string,
): string {
  if (!comparison) return '';
  if (comparison.unavailableReason) return ` ${comparison.unavailableReason}`;
  return ` Compared with ${formatDate(comparison.range.from)} – ${formatDate(comparison.range.to)}.`;
}

// ---------------------------------------------------------------------------
// Competitive position
// ---------------------------------------------------------------------------

/**
 * Whether the competitive block has any figure to show.
 *
 * ⚠️ ALL THREE NULL means the CARDS are not drawn — a card of three dashes
 * tells a client nothing and reads as a fault. ⚠️ But the SECTION still says why
 * (`competitiveUnavailable`), because impression share has a real source here
 * and a heading that silently disappears is the failure this report has already
 * had once.
 */
export function hasCompetitivePosition(c: PortalCompetitivePosition): boolean {
  return c.impressionShare !== null || c.budgetLostShare !== null || c.rankLostShare !== null;
}

/**
 * Why the competitive cards are absent.
 *
 * ⚠️ It names the CAUSE rather than apologising: Google reports auction share
 * for Search campaigns and not for Performance Max or Display, so a client whose
 * budget sits in PMax will legitimately never see these figures and should know
 * that rather than wonder what broke.
 */
export function competitiveUnavailable(): string {
  return (
    'Auction share is not measurable for these campaigns. Google reports impression share for ' +
    'Search campaigns; it does not report it for Performance Max or Display, so a period spent ' +
    'entirely on those has no share to show. Nothing is missing from the figures above.'
  );
}

/**
 * What each competitive figure means, in the client's own terms.
 *
 * ⚠️ These are the only numbers on the report that translate straight into a
 * decision, which is why they get prominent space where Meta's delivery metrics
 * are collapsed behind a disclosure. "You lost 15.7% of impressions to budget"
 * is an instruction; a CPM is not.
 */
export const COMPETITIVE_COPY = {
  impressionShare: {
    label: 'Impression share',
    explain: 'Of the searches you could have appeared for, this is the share you appeared for.',
  },
  budgetLostShare: {
    label: 'Lost to budget',
    explain:
      'Missed because the daily budget was already spent. This is the recoverable share.',
  },
  rankLostShare: {
    label: 'Lost to rank',
    explain: 'Missed on ad rank rather than money — bids, relevance and landing page.',
  },
} as const;

/**
 * ⚠️⚠️ A SHARE METRIC'S ZERO MEANS "NOT APPLICABLE", NOT 0%. Google writes 0.0
 * rather than NULL where a search share cannot apply, so the query layer already
 * nulls the impossible ones — and by the time a figure reaches here, null means
 * "we cannot measure this", which must never render as a measured zero.
 */
export function competitiveValueText(pct: number | null): string {
  return pct === null ? 'Not measurable for these campaigns' : `${pct.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * The provenance block's lines, in reading order.
 *
 * ⚠️ SHOWN TO THE CLIENT — a brand rule for monthly reports, and stronger on a
 * live page than in a PDF. A line whose `asOf` we could not read SAYS SO rather
 * than being dropped: an absent line reads as "that figure has no source", and a
 * guessed timestamp is worse than an admitted unknown.
 */
export function provenanceText(
  line: PortalProvenanceLine,
  formatDate: (iso: string) => string,
): string {
  const when = line.asOf ? `data to ${formatDate(line.asOf)}` : 'we could not read when this was last pulled';
  // ⚠️ PLATFORM, then date. The "which figures — which system" split went on
  // 2026-09-01 with the supplier names it carried; the platform IS the source
  // system as far as a client is concerned.
  return `${line.what}, ${when}`;
}

// ---------------------------------------------------------------------------
// Absence
//
// ⚠️⚠️ OMIT A SECTION WITH NO DATA SOURCE; EXPLAIN A SECTION WITH A SOURCE AND A
// REASON. The restyle brief's original instruction — "a section whose data is
// genuinely unavailable is omitted entirely" — was too broad, and on 2026-08-31
// it cost four rounds of investigation: a single-location audience panel with a
// real reason deleted itself in silence, and the only signal in the whole system
// was a human noticing a panel was missing on one club.
//
// The model to copy is `yearOnYear.previousUnavailableReason`, which explains its
// own absence in full, unprompted, in the same payload. So: a CRM section before
// the CRM exists is omitted (nothing to explain, and a placeholder would promise
// a feature); everything with a source says why it is empty.
//
// ⚠️ These are SENTENCES, not placeholders. No em-dash grid, no skeleton card,
// no "coming soon" — a visible gap is recoverable, a plausible empty section is
// not, and an unexplained gap is a support ticket at best.
// ---------------------------------------------------------------------------

// ⚠️⚠️ `audienceUnavailableForGroup()` IS GONE (round 8 §3, 2026-09-04). Its
// sentence explained why a MULTI-LOCATION report had no audience section, and
// the section is now OMITTED on a group report entirely — a composition
// decision made where the scope is known, so there is nothing left to explain.
// A section whose entire content is an apology is worse than no section.
//
// ⚠️ DELETED RATHER THAN LEFT UNCALLED: a tested-but-uncalled function reads as
// live to anyone auditing coverage (the reasoning that deleted `crmNestingNote`
// in round 7).
//
// ⚠️⚠️ `audienceUnavailableForAccount()` BELOW IS THE ONE THAT MUST NEVER GO.
// A SINGLE location's missing audience data is A FAULT SOMEONE SHOULD CHASE, not
// a structural absence, and silence on it is what turned a failed nightly job
// into four rounds of investigation on 2026-08-31.

/**
 * Why the audience section is absent when the breakdown could not be READ.
 *
 * ⚠️⚠️ A DIFFERENT STATEMENT FROM "no audience data was pulled". `resolveDemographics`
 * degrades a failed query to null so one broken breakdown cannot take the whole
 * Meta page down with it — which is right, and which used to be indistinguishable
 * from a client having no audience data at all, because the panel then rendered
 * nothing whatsoever. It says outright that this is our problem, not theirs:
 * silence here reads as "you had no audience", which is a claim about their
 * business rather than about our pipeline.
 */
export function audienceUnavailableForAccount(): string {
  return (
    "Audience breakdowns could not be loaded for this period. That is a problem at our end rather " +
    'than a fact about your campaigns — nothing else on this page is affected, and your account ' +
    'manager can chase it.'
  );
}

/**
 * Why the creative grid is empty.
 *
 * ⚠️ SPLIT BY CAUSE, because the two are different facts about the client's month
 * and a single sentence would have to be vague enough to fit both. A period with
 * spend but no creative rows is a gap in what Meta returned to us; a period with
 * no spend at all is simply a month when nothing ran, which is not a fault and
 * must not be dressed up as one.
 */
export function creativesUnavailable(hadSpend: boolean): string {
  return hadSpend
    ? 'No individual ads were recorded for this period. The campaign figures above are unaffected — ' +
        'Meta returns creative detail separately, and it can be absent for a period whose spend is ' +
        'fully accounted for.'
    : 'No ads ran in this period, so there are no creatives to show.';
}

/**
 * Why the delivery-diagnostics disclosure has nothing in it.
 *
 * ⚠️ ONLY reached when campaigns exist. With no campaigns at all there is no
 * source and the disclosure is omitted entirely — the other half of the rule.
 */
export function deliveryDiagnosticsUnavailable(): string {
  return (
    'No impressions or clicks were recorded for this period, so there is nothing to break down here. ' +
    'This describes delivery only; it does not affect the leads or spend above.'
  );
}

/**
 * ⚠️⚠️ THE SUFFIX THAT REPLACED THE CLUB-LEVEL NOTE (§1c, 2026-09-04).
 *
 * It used to be a nineteen-word line under the row: *"Conversion rate and both
 * cost figures are against the club’s own totals, from every source — not the
 * ads alone."* Scott asked for the band’s three notes gone. Two went outright;
 * this one could not, and the reason is commercial rather than editorial.
 *
 * ⚠️⚠️ `COST PER SALE` WITH NOTHING QUALIFYING IT READS AS "the advertising cost
 * $21.73 per member". IT IS NOT. It is ALL media spend over EVERY sale the club
 * recorded — walk-ins, referrals and members who found them without an ad — and
 * the CRM records no channel on a sale row (measured: 61,350 of 61,350 blank),
 * so the ad-only figure is UNKNOWABLE and HIGHER. This is the number on the
 * report most likely to be quoted back at Kyck in a renewal negotiation, as
 * what the advertising costs per member.
 *
 * ⚠️⚠️ SO THE DISCLOSURE MOVED INTO THE LABEL, WHICH IS STRONGER THAN THE
 * FOOTNOTE IT REPLACES rather than a compromise with it. A label travels with
 * its figure into a screenshot and into a renewal conversation; a sentence
 * under the row does not, and is not read. Three words instead of a paragraph.
 *
 * ⚠️ ONLY THE THREE RATIOS TAKE IT. `Total media spend` is the NUMERATOR and
 * genuinely IS ads only — suffixing it would be the opposite error, disclaiming
 * a figure that needs no disclaimer and diluting the three that do.
 */
export const ALL_SOURCES_SUFFIX = ' · All sources';

export function allSourcesLabel(label: string): string {
  return `${label}${ALL_SOURCES_SUFFIX}`;
}

// ⚠️⚠️ `crmNestingNote()` IS GONE (§1a, 2026-09-04), AND ITS RETURN-CONDITION
// COMMENT WENT WITH IT DELIBERATELY — leaving it would have the next reader
// restore the sentence a third time.
//
// It said *"Internet leads are the enquiries that arrived through an online
// form. Your ads are part of that, but the CRM does not record which."* It had
// been deleted once and restored once, the restore argued on the grounds that it
// was "now the ONLY thing on the page saying that internet leads are a SUBSET".
//
// ⚠️⚠️ THAT CLAIM WAS FALSE, AND THE DISPROOF WAS THIRTY-TWO LINES BELOW IT IN
// THE SAME COMPONENT: `<NestingBar>` renders the total decomposed into the
// subset and the remainder — "519 INTERNET LEADS · 78.8%" beside "140 FROM
// EVERYWHERE ELSE · 21.2%", against a proportional bar. A LABELLED PROPORTION
// BAR THAT NAMES THE REMAINDER IS A STRONGER STATEMENT OF SUBSET-HOOD THAN ANY
// SENTENCE: it shows the nesting, and cannot be skim-read past.
//
// ⚠️⚠️ THE LESSON, WHICH IS WHY THIS COMMENT REPLACES THE FUNCTION: A COMMENT
// ASSERTING "THIS IS NOW THE ONLY X ON THE PAGE" IS A CLAIM ABOUT THE RENDERED
// PAGE, AND THE FILE IT SITS IN CANNOT VERIFY IT. The restore was argued from
// the comment rather than from the page, with the NestingBar visible in the
// screenshot the whole time — the same shape as the three earlier omissions
// justified by pointing at a surface nobody had checked.
//
// ⚠️ So the condition for a return is NOT "the layout stopped carrying it". It
// is "the NestingBar is gone". The nesting is total leads ⊃ internet leads
// ⊃ ad-attributed leads, and the innermost set cannot be computed.

// ⚠️⚠️ `crmComparisonBasisNote()` IS GONE (§1b, 2026-09-04). It said *"Compared
// with the same period last year, 1 Aug 2025 — 31 Aug 2025."* — and §5b had
// already put exactly that fact in the REPORT HEADER, beside the period it
// qualifies, which is where Scott asked for it ("next to the report dates…
// so we know what data we are looking at"). The page stated it twice, which is
// why it read as adding nothing.
//
// ⚠️ THE HEADER LINE IS THE REQUIREMENT AND STAYS: `periodNote`'s
// `comparisonClause` names the window, read from the PAYLOAD rather than
// recomputed, and prints the REASON instead of the dates when there is no
// usable comparison.
//
// ⚠️ Nothing is lost in the no-comparison case: this returned null when no
// delta was on the band, so an unusable comparison was already silent here.
// ⚠️ (This block used to carry a "`previousUnavailableReason` is rendered
// NOWHERE — FLAGGED, NOT FIXED" note. Round 8 §2 rendered it, in `CrmHeroBand`,
// above the figures. The note outlived its truth and was then read back as a
// live finding, which is why it is gone rather than amended: A COMMENT
// DESCRIBING A DEFECT MUST BE DELETED IN THE COMMIT THAT FIXES IT.)

/**
 * Why a delivery card shows a figure and no chart.
 *
 * ⚠️⚠️ NOT A CHART NOTE — it is a fact about the DATA (there is not enough of it
 * to draw), rendered where the chart would be. The mechanics footnotes that used
 * to sit under every chart are gone; this and `withheldReason` are what stayed,
 * and the distinction is the whole point of that removal.
 *
 * ⚠️ It reverses the rule that shipped with the sparklines: five silent gaps
 * under five small marks were unremarkable, five full chart CARDS with a figure
 * and a blank space beneath read as broken. See `MIN_CHART_MONTHS`.
 *
 * ⚠️ It explains the CHART's absence and says nothing about the figure, which is
 * complete and needs no qualifying.
 */
export function deliveryHistoryWithheld(measured: number, grain: ChartGrain): string {
  // ⚠️ It names the bucket the reader is actually looking at. Saying "months"
  // under a reporting-period chart describes something that is not on screen.
  const noun = grain === 'months' ? 'month' : 'day';
  if (measured === 0) {
    return `Nothing measured ${grain === 'months' ? 'in the last 12 months' : 'in this period'}, so there is nothing to chart.`;
  }
  return `Only ${measured} ${noun}${measured === 1 ? '' : 's'} measured — too few to show a shape worth reading.`;
}

// ---------------------------------------------------------------------------
// The "Where the budget goes" grain toggle
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ WHAT THE AD-GROUP VIEW LEAVES OUT, NAMED WITH ITS COST. This sentence is
 * what makes the toggle safe rather than a trap.
 *
 * Performance Max campaigns have no ad groups at all, so switching to Ad group
 * REMOVES their spend from the table — it is not a rearrangement of the same
 * money. A client who flips a switch and watches a total drop, with nothing on
 * screen to say why, has been handed a puzzle they will solve as "one of these
 * numbers is wrong".
 *
 * ⚠️ IT POINTS BACK AT THE COMPLETE VIEW. Naming the cost without saying where
 * to find it leaves the reader stuck; "the campaign view shows all of it" is the
 * half that resolves it.
 *
 * ⚠️ COST IS PER CURRENCY, joined rather than summed — a group login can span AU
 * and NZ and this report never blends.
 *
 * `formatMoney` is passed in because currency formatting is a render concern; the
 * sentence is asserted here without it.
 */
export function adGroupExclusionNote(
  excluded: { campaigns: number; cost: Array<{ currency: string; amount: number }>; names: string[] },
  formatMoney: (amount: number, currency: string) => string,
): string {
  const cost = excluded.cost.map((c) => formatMoney(c.amount, c.currency)).join(' · ');
  const n = excluded.campaigns;
  const which = excluded.names.length > 0 ? ` (${excluded.names.join(', ')})` : '';
  return (
    `Google does not break Performance Max campaigns into ad groups, so ${cost} across ` +
    `${n} campaign${n === 1 ? '' : 's'}${which} is not in this view. ` +
    'The campaign view shows all of it.'
  );
}

// ---------------------------------------------------------------------------
// The CRM panel
//
// ⚠️⚠️ THESE SENTENCES ARE THE FEATURE, NOT DECORATION. A sale in GymSales
// carries no source, no contact_type and no person_id — measured, 188,726 of
// 188,726 rows blank in all three — so every figure on this panel is a
// CLUB-LEVEL figure that a franchisee will otherwise read as an ADVERTISING
// figure. Conversion rate is the dangerous one in the pessimistic direction (an
// ad rate read over every walk-in looks far worse than the ads did) and cost per
// sale in the flattering one (all media over all sales looks like our media
// bought all of them). Both are honest only while the page says which they are.
//
// The brief is explicit: if the cost-per-sale caveat cannot be fitted on screen,
// the metric does not ship.
// ---------------------------------------------------------------------------

/**
 * What the conversion rate is a rate OF.
 *
 * ⚠️ It names the denominator in words, and the two lead figures sit beside it
 * on the panel so a reader can see the gap between all enquiries and digital
 * ones for themselves. That is the denominator-beside-the-rate rule, and here it
 * is doing real work rather than being a formality.
 */
/**
 * Why a per-channel hero figure carries no change (§3, 2026-09-03).
 *
 * ⚠️⚠️ SHOWN INSTEAD OF A PERCENTAGE, never beside one. A channel with no
 * measured figure in the selected period renders an em dash as its VALUE, and a
 * change beside a dash is nonsense — worse, it would be computed against a real
 * prior period and read as a collapse to zero, which is rule 15 wearing a
 * percentage.
 *
 * ⚠️ THIS IS GOOGLE'S SITUATION TODAY — its history starts 2026-06-30 — and it
 * resolves itself as the backfill lands. So this is the honest render of a real
 * state, not a placeholder for one.
 */
export function channelUnmeasuredInPeriod(channel: string): string {
  return `We have no ${channel} figures for this period, so there is nothing to compare.`;
}

/**
 * ⚠⚠ A READER GIVEN PER-CLUB AND COMBINED FIGURES ON ONE PAGE WILL SUBTRACT.
 *
 * The counts DO add up to the headline; cost per lead does NOT, because it is
 * re-derived per club from that club's own pair rather than averaged. Saying so
 * in one line is cheaper than letting a manager discover a column that does not
 * reconcile and conclude the report is wrong.
 *
 * ⚠ ONE SHORT LINE, deliberately. §4d of the brief: if the leaderboard reads
 * as a second results section, the fix is HIERARCHY — a lighter surface, further
 * down — not a longer caveat.
 */
export function leaderboardNestingNote(labels: PortalReportLabels): string {
  const leads = labels.leadsShort.toLowerCase();
  return `Each club's ${leads} and spend add up to the totals above. ${labels.costPerLead} is worked out per club, so it does not.`;
}

/**
 * Why some clubs show dashes rather than figures.
 *
 * ⚠⚠ STATED ONCE, BELOW THE TABLE, AND ONLY WHEN IT APPLIES. A dash with no
 * explanation reads as a fault; the same sentence on every report would train
 * people to ignore it.
 *
 * ⚠ Names the clubs rather than counting them — a manager scanning for their
 * own club needs to find it, and there are at most a couple of dozen.
 */
export function leaderboardCoverageNote(unmeasured: readonly string[]): string | null {
  if (unmeasured.length === 0) return null;
  const list = unmeasured.join(', ');
  return unmeasured.length === 1
    ? `We have no figures for ${list} in this period, so its row shows dashes rather than zeros.`
    : `We have no figures for these clubs in this period, so their rows show dashes rather than zeros: ${list}.`;
}

/**
 * Why some clubs show dashes in the CRM columns specifically.
 *
 * ⚠⚠ A DIFFERENT SENTENCE FROM THE COVERAGE ONE, and they are not
 * interchangeable: "we were not measuring your advertising" and "this club's CRM
 * is not connected to this report" send a reader to two different people to fix
 * two different things. `uncountedClubs` already travels on the payload for
 * exactly this reason.
 */
export function leaderboardCrmNote(uncounted: readonly string[]): string | null {
  if (uncounted.length === 0) return null;
  const list = uncounted.join(', ');
  return uncounted.length === 1
    ? `${list} is not linked to the club system yet, so its enquiry and sale columns are blank.`
    : `These clubs are not linked to the club system yet, so their enquiry and sale columns are blank: ${list}.`;
}

/**
 * The denominator line under a group-average tile.
 *
 * ⚠⚠ THE TILE MUST STATE ITS DENOMINATOR, and the denominator is the clubs
 * WITH DATA rather than the clubs in scope. A club that has never run ads
 * dragging the average down is rule 15 at aggregate scale — an unmeasured thing
 * counted as a measured nothing — and an average whose divisor is invisible
 * cannot be checked by the person reading it.
 */
export function groupAverageDenominator(clubs: number, clubsInScope: number): string {
  if (clubs === 0) return `No clubs measured yet, of ${clubsInScope}`;
  const noun = clubs === 1 ? 'club' : 'clubs';
  return clubs === clubsInScope
    ? `Across all ${clubs} ${noun}`
    : `Across the ${clubs} ${noun} with figures, of ${clubsInScope}`;
}

/**
 * The partial-coverage sentence.
 *
 * ⚠ A SEPARATE SENTENCE FROM THE NO-DATA ONE, because they are different
 * facts: `no-data` means the figures are absent, `partial` means they are REAL
 * BUT UNDERSTATE. Collapsing the two would either hide a real result or
 * withhold a figure we have.
 */
export function leaderboardPartialNote(partial: readonly string[]): string | null {
  if (partial.length === 0) return null;
  return partial.length === 1
    ? `${partial[0]}'s reporting starts inside this period, so its figures are real but cover only part of it.`
    : `These clubs' reporting starts inside this period, so their figures are real but cover only part of it: ${partial.join(', ')}.`;
}

export function crmConversionRateNote(labels: PortalReportLabels): string {
  return (
    `${labels.conversionRate} is ${labels.sales.toLowerCase()} against every enquiry the club ` +
    'recorded, from every source — walk-ins, referrals and phone calls, not only the ads. ' +
    'The two lead figures beside it show how much of that came through an online form.'
  );
}

/**
 * What the cost per sale divides.
 *
 * ⚠️⚠️ WITHOUT THIS SENTENCE THE METRIC DOES NOT SHIP. The client's CRM cannot
 * say which channel a sale came from, so this is all media over all sales — and
 * read as "what our advertising paid per sale" it materially overstates what the
 * advertising did.
 */
export function crmCostPerSaleNote(labels: PortalReportLabels): string {
  return (
    `${labels.costPerSale} divides ALL media spend in this period by ALL ${labels.sales.toLowerCase()} ` +
    'the club recorded — including walk-ins, referrals and anyone who found you without an ad. ' +
    'Your CRM does not record which channel a sale came from, so this is not what the advertising ' +
    'alone paid per sale.'
  );
}

/**
 * Which clubs a group's CRM figures actually cover, or null when they cover all
 * of them.
 *
 * ⚠️⚠️ AN UNLINKED CLUB WOULD OTHERWISE CONTRIBUTE A SILENT ZERO to every figure
 * here, and drag the conversion rate down with it. That is rule 15 at scope
 * level — an unmeasured thing rendered as a measured nothing — so the panel
 * names the shortfall rather than presenting a partial total as a whole one.
 *
 * ⚠️ It names the UNCOUNTED clubs, not the counted ones: those are the ones
 * someone can act on, and on a forty-club login the counted list is the noise.
 * Capped, with the remainder counted, because a wall of names is read as none.
 */
export function crmScopeNote(
  counted: number,
  uncounted: ReadonlyArray<{ name: string }>,
  max = 4,
): string | null {
  if (uncounted.length === 0) return null;
  const total = counted + uncounted.length;
  const shown = uncounted.slice(0, max).map((c) => c.name);
  const rest = uncounted.length - shown.length;
  const names = rest > 0 ? `${shown.join(', ')} and ${rest} more` : shown.join(', ');
  return (
    `These figures cover ${counted} of your ${total} locations. ` +
    `${names} ${uncounted.length === 1 ? 'is' : 'are'} not connected to the CRM yet, so ` +
    'nothing from there is counted here — rather than being counted as zero. ' +
    'Your account manager can connect them.'
  );
}

/**
 * Why part — or all — of the selected period has no CRM figures.
 *
 * ⚠️⚠️ AN UNMEASURED PERIOD IS NOT A QUIET MONTH. CRM history does not start at
 * the same date for every club (New Zealand's begins in January 2024, Australia's
 * in 2021), so a client looking at last spring can be looking at a period we hold
 * nothing for. Rendering that as zero enquiries and zero sales tells a business
 * owner nothing happened, which is a claim about their club rather than about our
 * data.
 *
 * ⚠️ THE WHOLE-PERIOD CASE AND THE PART-PERIOD CASE ARE DIFFERENT SENTENCES,
 * because one means "these figures are wrong to read at all" and the other means
 * "these figures are real but low". Collapsing them would misdescribe one every
 * time. Returns null when the period is covered throughout.
 */
export function crmCoverageNote(
  range: { from: string; to: string },
  coverage: { firstDate: string | null; fullFromDate: string | null },
  formatDate: (iso: string) => string,
): string | null {
  if (!coverage.firstDate) {
    return (
      'We hold no CRM history for this club yet, so there are no leads or sales to show. ' +
      'That is an absence of records, not an absence of activity.'
    );
  }
  if (range.to < coverage.firstDate) {
    return (
      `This club's CRM records start on ${formatDate(coverage.firstDate)}, which is after the ` +
      'period you have selected — so there is nothing to count here. Nothing below is a measure of ' +
      'a quiet month.'
    );
  }
  if (range.from < coverage.firstDate) {
    return (
      `This club's CRM records start on ${formatDate(coverage.firstDate)}, part-way through the ` +
      'period you have selected. Everything below counts from that date onward, so it is real but ' +
      'lower than a full period would be.'
    );
  }
  if (coverage.fullFromDate && range.from < coverage.fullFromDate) {
    return (
      `Not every location's CRM records go back to the start of this period — the earliest they ` +
      `all cover is ${formatDate(coverage.fullFromDate)}. The totals below are real but understate ` +
      'the months before that.'
    );
  }
  return null;
}
